import { Link } from 'react-router';
import type { Service } from '@bekebi/contracts/api';
import { EmptyState, ErrorState, LoadingState } from '../../components/states';
import { useHasPermission } from '../../session/current-user';
import { useServices } from './api';
import { hasServiceHealth } from '@bekebi/domain';
import { HealthBadge, LastChecked, ServiceTypeIcon } from './components';
import { StorageSummaryLine } from './storage-usage';
import { HEALTH_REASON_TEXT, formatDate, renewalStatusText } from './format';
import { buttonClass } from '../../components/button';

/**
 * Services overview (/app/services): every service from GET /api/services with its health (as the
 * API derived it), newest check time and renewal summary. No figure here is computed or invented.
 */
export function ServicesOverview() {
  const services = useServices();
  const canManage = useHasPermission('services.manage');

  const addLink = canManage ? (
    <Link to="/app/services/new" className={buttonClass('primary')}>
      Add service
    </Link>
  ) : null;

  return (
    <div className="max-w-5xl">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1>Services</h1>
        {services.data && services.data.length > 0 ? addLink : null}
      </div>
      <p className="mt-1 text-sm text-muted">
        Bekebi&rsquo;s services, with health from the latest real check results.
      </p>

      <div className="mt-6">
        {services.isPending ? (
          <LoadingState label="Loading services…" />
        ) : services.isError ? (
          <ErrorState
            title="Services could not be loaded"
            message={services.error.message}
            onRetry={() => void services.refetch()}
          />
        ) : services.data.length === 0 ? (
          <EmptyState
            title="No services yet"
            description="No services are recorded in the portal."
            action={addLink}
          />
        ) : (
          <ul aria-label="Service list" className="grid gap-4 md:grid-cols-2">
            {services.data.map((service) => (
              <li key={service.id}>
                <ServiceCard service={service} />
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

function ServiceCard({ service }: { service: Service }) {
  return (
    <article className="flex h-full flex-col rounded-[var(--radius-card)] border border-line bg-surface p-5 transition-colors hover:border-line-strong">
      <div className="flex items-start gap-3">
        <ServiceTypeIcon type={service.type} />
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h2>
              <Link
                to={`/app/services/${service.id}`}
                className="rounded-sm text-fg underline-offset-4 hover:text-accent-link hover:underline"
              >
                {service.name}
              </Link>
            </h2>
            {hasServiceHealth(service.type) ? <HealthBadge health={service.health} /> : null}
          </div>
          <p className="mt-1 text-sm text-fg-2">
            {hasServiceHealth(service.type) ? (
              HEALTH_REASON_TEXT[service.health.reason]
            ) : (
              <StorageSummaryLine serviceId={service.id} />
            )}
          </p>
        </div>
      </div>
      <dl className="mt-4 space-y-2 border-t border-line pt-4 text-sm">
        {hasServiceHealth(service.type) ? (
          <div className="flex flex-wrap gap-x-2">
            <dt className="text-muted">Last checked</dt>
            <dd className="text-fg-2">
              <LastChecked checkedAt={service.health.checkedAt} />
            </dd>
          </div>
        ) : null}
        <div className="flex flex-wrap gap-x-2">
          <dt className="text-muted">Renewal</dt>
          <dd className="text-fg-2">
            {service.renewal
              ? `${formatDate(service.renewal.renewalDate)} · ${renewalStatusText(service.renewal.condition, service.renewal.countdown)}`
              : 'No open renewal term'}
          </dd>
        </div>
        {service.type === 'website' ? (
          <div className="flex flex-wrap gap-x-2">
            <dt className="text-muted">Address</dt>
            <dd className="break-all font-mono text-fg-2">{service.accessUrl ?? 'Not set'}</dd>
          </div>
        ) : null}
      </dl>
    </article>
  );
}
